/**
 * Timing card for tentative shows with editable timing fields
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Alert,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import { Event as EventIcon, Edit as EditIcon } from '@mui/icons-material';
import type { HoldList, Hold } from 'api-client/models';
import { formatDateLong } from '@/utils/dateUtils';
import { formatTime } from '../../../utils/formatters';
import { TimeField } from '../forms/TimeField';
import { InlineEditingControls } from '../forms/InlineEditingControls';

interface DateTimingCardProps {
  hold: HoldList | null;
  holdDetails: Hold | null; // Full hold details with timing fields
  isEditing: boolean;
  formData: Record<string, string | number | boolean | null | undefined>;
  onStartEdit: (section: string, holdId: string) => Promise<void>;
  onSave: (section: string) => Promise<void>;
  onCancel: () => void;
  onUpdateField: (field: string, value: string | number | boolean | null | undefined) => void;
  onCopyToAll?: () => Promise<void>;
  otherConfirmedHoldCount: number;
  hasPendingHolds: boolean;
}

export const DateTimingCard: React.FC<DateTimingCardProps> = ({
  hold,
  holdDetails,
  isEditing,
  formData,
  onStartEdit,
  onSave,
  onCancel,
  onUpdateField,
  onCopyToAll,
  otherConfirmedHoldCount,
  hasPendingHolds,
}) => {
  if (!hold) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Date Timing
          </Typography>
          <Alert severity="info">Select a date above to view timing information.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <EventIcon />
          <Typography variant="h6" component="span">
            Date Timing
          </Typography>
          <Typography variant="body2" color="text.secondary" component="span">
            - {formatDateLong(hold.holdDate)}
          </Typography>
          {!isEditing && (
            <IconButton size="small" color="primary" aria-label="Edit timing details"
              onClick={() => void onStartEdit('timing', hold.id)}>
              <EditIcon />
            </IconButton>
          )}
        </Stack>

        {hasPendingHolds && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This show has pending holds. Timing information is tentative until all dates are confirmed.
          </Alert>
        )}

        {isEditing ? (
          // Edit mode
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TimeField
                  label="Load-In Time"
                  value={String(formData.loadInTime || '')}
                  onChange={(e) => onUpdateField('loadInTime', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TimeField
                  label="Soundcheck Time"
                  value={String(formData.soundcheckTime || '')}
                  onChange={(e) => onUpdateField('soundcheckTime', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TimeField
                  label="Doors Time"
                  value={String(formData.doorsTime || '')}
                  onChange={(e) => onUpdateField('doorsTime', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TimeField
                  label="Show Start Time"
                  value={String(formData.showTime || '')}
                  onChange={(e) => onUpdateField('showTime', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TimeField
                  label="Show End Time"
                  value={String(formData.endTime || '')}
                  onChange={(e) => onUpdateField('endTime', e.target.value)}
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                {otherConfirmedHoldCount > 0 && onCopyToAll && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void onCopyToAll()}
                  >
                    Copy Times to All {otherConfirmedHoldCount + 1} Dates
                  </Button>
                )}
              </Box>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <InlineEditingControls onSave={() => onSave('timing')} onCancel={onCancel} />
              </Stack>
            </Stack>
          </Stack>
        ) : (
          // View mode - use holdDetails which has timing fields
          <Grid container spacing={2}>
            <Grid 
              size={{ xs: 12, sm: 6, md: 4 }}
              onClick={() => void onStartEdit('timing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Load-In Time
              </Typography>
              <Typography variant="body2">
                {holdDetails?.loadInTime ? formatTime(holdDetails.loadInTime) : 'Not set'}
              </Typography>
            </Grid>
            <Grid 
              size={{ xs: 12, sm: 6, md: 4 }}
              onClick={() => void onStartEdit('timing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Soundcheck Time
              </Typography>
              <Typography variant="body2">
                {holdDetails?.soundcheckTime ? formatTime(holdDetails.soundcheckTime) : 'Not set'}
              </Typography>
            </Grid>
            <Grid 
              size={{ xs: 12, sm: 6, md: 4 }}
              onClick={() => void onStartEdit('timing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Doors Time
              </Typography>
              <Typography variant="body2">
                {holdDetails?.doorsTime ? formatTime(holdDetails.doorsTime) : 'Not set'}
              </Typography>
            </Grid>
            <Grid 
              size={{ xs: 12, sm: 6, md: 4 }}
              onClick={() => void onStartEdit('timing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Show Start Time
              </Typography>
              <Typography variant="body2">
                {holdDetails?.showTime ? formatTime(holdDetails.showTime) : 'Not set'}
              </Typography>
            </Grid>
            <Grid 
              size={{ xs: 12, sm: 6, md: 4 }}
              onClick={() => void onStartEdit('timing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Show End Time
              </Typography>
              <Typography variant="body2">
                {holdDetails?.endTime ? formatTime(holdDetails.endTime) : 'Not set'}
              </Typography>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
